'use strict'

const { scanCritic, projectComments, commentAnchorHash } = require('./critic-markup')
const { normalizeSource } = require('./critic-source')

/**
 * Escape a string for safe use inside an HTML attribute.
 * @param {string} str
 * @returns {string}
 */
function escapeAttr (str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\r/g, '&#13;')
}

function projectedCritic (state, silent) {
  const projection = state.env.criticProjection
  if (!projection || !state.src.startsWith(projection.prefix, state.pos)) return false
  const end = state.src.indexOf('!}', state.pos) + 2
  if (end < 2 || end > state.posMax) return false
  const span = projection.markers[state.src.slice(state.pos, end)]
  if (!span) return false
  let to = end
  if (span.type !== 'comment') {
    const raw = normalizeSource(span.raw)
    to += raw.length
    if (to > state.posMax || state.src.slice(end, to) !== raw) return false
  }
  if (!silent) {
    const token = state.push('critic_' + span.type, '', 0)
    token.content = normalizeSource(span.content)
    token.meta = span.messages
    token.criticSpan = span
    token.markup = span.raw
  }
  state.pos = to
  return true
}

/**
 * markdown-it plugin that renders CriticMarkup: comments become inline
 * comment icons, suggestions (ins/del/sub) and highlights become styled
 * spans carrying their raw markup in data-critic-raw.
 *
 * @param {import('markdown-it')} md
 */
module.exports = function criticCommentPlugin (md) {
  md.core.ruler.before('normalize', 'critic_project', state => {
    const spans = scanCritic(state.src)
    const projection = projectComments(state.src, spans, true)
    const seenRaw = new Map()
    projection.ordinals = Object.create(null)
    for (const span of spans) {
      const ordinal = seenRaw.get(span.raw) || 0
      projection.ordinals[span.from] = ordinal
      seenRaw.set(span.raw, ordinal + 1)
    }
    state.env.criticProjection = projection
    state.src = projection.source
  })
  md.core.ruler.push('critic_restore', state => {
    const projection = state.env.criticProjection
    const comments = []
    const restoreText = text => text.replace(/\{!critic\d+:\d+ ?!\}/g, marker => {
      const span = projection.markers[marker]
      if (!span) return marker
      return span.type === 'comment' ? span.raw : ''
    })
    function restore (tokens) {
      for (const token of tokens) {
        if (token.map) token.map = token.map.map(projection.originalLine)
        if (token.type !== 'critic_comment') token.content = restoreText(token.content)
        if (token.attrs) token.attrs = token.attrs.map(([name, value]) => [name, restoreText(String(value))])
        if (token.criticSpan) token.criticOrdinal = projection.ordinals[token.criticSpan.from]
        if (token.type === 'critic_comment' && !token.criticSpan.resolved && token.meta.length) comments.push(token)
        if (token.children) restore(token.children)
      }
    }
    restore(state.tokens)
    const seenHashes = Object.create(null)
    const seenLines = Object.create(null)
    comments.sort((a, b) => a.criticSpan.from - b.criticSpan.from)
    for (const token of comments) {
      const first = token.meta[0]
      const hash = commentAnchorHash(first.author, first.text)
      const nth = (seenHashes[hash] = (seenHashes[hash] || 0) + 1)
      const line = token.criticSpan.line
      const lineNth = (seenLines[line] = (seenLines[line] || 0) + 1)
      token.criticAnchor = 'comment-' + hash + (nth > 1 ? '-' + nth : '')
      token.criticAlias = 'cl' + line + (lineNth > 1 ? '-' + lineNth : '')
    }
  })
  md.inline.ruler.before('text', 'critic_projected_comment', projectedCritic)

  const esc = md.utils.escapeHtml

  md.renderer.rules.critic_comment = function (tokens, idx) {
    const span = tokens[idx].criticSpan
    const messages = tokens[idx].meta
    if (!messages.length) return ''
    const first = messages[0]
    const thread = ' data-comment="' + escapeAttr(first.text) + '"' +
      (first.author ? ' data-author="' + escapeAttr(first.author) + '"' : '') +
      ' data-thread="' + escapeAttr(JSON.stringify(messages)) + '"'
    const count = messages.length > 1
      ? '<span class="critic-comment-count">' + messages.length + '</span>'
      : ''
    // A resolved thread stays in the source, so it gets a muted read-only pill
    // (hidden until the reader asks for it) instead of vanishing. Deliberately
    // no id, no anchor ordinal consumed and not .critic-comment: reply/resolve
    // identity, margin bubbles and spec-board's threadAnchors all key off
    // those.
    if (span.resolved) {
      return '<span class="critic-comment-resolved" title="Resolved comment"' + thread +
        '><i class="fa fa-check"></i>' + count + '</span>'
    }
    const anchorId = tokens[idx].criticAnchor
    const alias = tokens[idx].criticAlias
    return '<span id="' + anchorId + '" class="critic-comment"' + thread +
      ' data-critic-raw="' + escapeAttr(tokens[idx].markup) + '"' +
      ' data-comment-ordinal="' + tokens[idx].criticOrdinal + '"' +
      ' data-comment-from="' + span.from + '" data-comment-to="' + span.to + '"' +
      ' data-comment-line="' + span.line + '" data-comment-endline="' + span.endLine + '"' +
      ' data-comment-alias="' + alias + '"' +
      '><span id="' + alias + '" class="critic-comment-alias"></span><i class="fa fa-comment"></i>' + count + '</span>'
  }

  md.renderer.rules.critic_ins = function (tokens, idx) {
    const t = tokens[idx]
    return '<ins class="critic-ins" data-critic-raw="' + escapeAttr(t.markup) + '" data-critic-ordinal="' + t.criticOrdinal + '">' + esc(t.content) + '</ins>'
  }

  md.renderer.rules.critic_del = function (tokens, idx) {
    const t = tokens[idx]
    return '<del class="critic-del" data-critic-raw="' + escapeAttr(t.markup) + '" data-critic-ordinal="' + t.criticOrdinal + '">' + esc(t.content) + '</del>'
  }

  md.renderer.rules.critic_sub = function (tokens, idx) {
    const t = tokens[idx]
    const sep = t.content.indexOf('~>')
    const oldText = sep === -1 ? t.content : t.content.slice(0, sep)
    const newText = sep === -1 ? t.content : t.content.slice(sep + 2)
    return '<span class="critic-sub" data-critic-raw="' + escapeAttr(t.markup) + '" data-critic-ordinal="' + t.criticOrdinal + '">' +
      '<del class="critic-sub-old">' + esc(oldText) + '</del>' +
      '<ins class="critic-sub-new">' + esc(newText) + '</ins>' +
      '</span>'
  }

  md.renderer.rules.critic_highlight = function (tokens, idx) {
    return '<mark class="critic-highlight">' + esc(tokens[idx].content) + '</mark>'
  }
}
